import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, MapPin, Clock, CheckCircle, XCircle, AlertCircle, ClipboardCheck, Download, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ZenButton from '../components/ZenButton';

const DashboardEvaluateur = ({ user }) => {
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/evaluateur/dashboard');
      setDashData(res.data);
    } catch { toast.error("Erreur chargement du tableau de bord"); }
    finally { setLoading(false); }
  };

  const handleConvocationResponse = async (convocationId, statut) => {
    try {
      await axios.put(`/convocations/${convocationId}`, { statut });
      toast.success(statut === 'acceptee' ? 'Convocation acceptee !' : 'Convocation refusee');
      loadDashboard();
    } catch { toast.error("Erreur lors de la mise a jour"); }
  };

  const generateICalLink = (conv) => {
    const date = conv.date_epreuve?.replace(/-/g, '') || '';
    const heure = conv.heure?.replace(':', '') || '0900';
    const startDT = `${date}T${heure}00`;
    // End = start + 3h
    const endH = String(Math.min(23, parseInt(heure.substring(0, 2)) + 3)).padStart(2, '0');
    const endDT = `${date}T${endH}${heure.substring(2)}00`;
    const title = encodeURIComponent(`EPMSP - ${conv.type_epreuve || 'Evaluation'}`);
    const location = encodeURIComponent(conv.lieu || '');
    const details = encodeURIComponent(`Convocation EPMSP FISL\n${conv.notes || ''}`);
    
    const ical = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//FISL//EPMSP//FR',
      'BEGIN:VEVENT',
      `DTSTART:${startDT}`,
      `DTEND:${endDT}`,
      `SUMMARY:EPMSP - ${conv.type_epreuve || 'Evaluation'}`,
      `LOCATION:${conv.lieu || ''}`,
      `DESCRIPTION:Convocation EPMSP FISL. ${conv.notes || ''}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([ical], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `convocation_epmsp_${date}.ics`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Fichier .ics telecharge ! Ouvrez-le pour ajouter a votre agenda.');
  };

  const generateGoogleCalLink = (conv) => {
    const date = conv.date_epreuve?.replace(/-/g, '') || '';
    const heure = conv.heure?.replace(':', '') || '0900';
    const startDT = `${date}T${heure}00`;
    const endH = String(Math.min(23, parseInt(heure.substring(0, 2)) + 3)).padStart(2, '0');
    const endDT = `${date}T${endH}${heure.substring(2)}00`;
    const title = encodeURIComponent(`EPMSP - ${conv.type_epreuve || 'Evaluation'}`);
    const location = encodeURIComponent(conv.lieu || '');
    const details = encodeURIComponent(`Convocation EPMSP FISL\n${conv.notes || ''}`);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDT}/${endDT}&location=${location}&details=${details}`;
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'envoyee': return <Badge className="bg-amber-100 text-amber-700 border-amber-200">En attente</Badge>;
      case 'acceptee': return <Badge className="bg-green-100 text-green-700 border-green-200">Acceptee</Badge>;
      case 'refusee': return <Badge className="bg-red-100 text-red-700 border-red-200">Refusee</Badge>;
      default: return <Badge variant="outline">{statut}</Badge>;
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div>
    </div>
  );

  const stats = dashData?.stats || {};
  const convocations = dashData?.convocations || [];

  return (
    <div className="space-y-6" data-testid="dashboard-evaluateur">
      <ZenButton />
      {/* Welcome */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold">Bienvenue, {user?.prenom}</h1>
        <p className="text-slate-300 mt-1">Espace Evaluateur EPMSP - FISL</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <ClipboardCheck className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total_convocations || 0}</p>
              <p className="text-sm text-slate-500">Convocations</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.en_attente || 0}</p>
              <p className="text-sm text-slate-500">En attente</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.acceptees || 0}</p>
              <p className="text-sm text-slate-500">Acceptees</p>
            </div>
          </div>
        </div>
      </div>

      {/* Convocations list */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-5 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">Mes convocations</h2>
        </div>

        {convocations.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <ClipboardCheck className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>Aucune convocation pour le moment</p>
            <p className="text-sm mt-1">Vous serez notifie lorsqu'une convocation vous sera envoyee</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {convocations.map((conv) => (
              <div key={conv.convocation_id} className="p-5 hover:bg-slate-50 transition-colors" data-testid={`convocation-${conv.convocation_id}`}>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800">{conv.type_epreuve || 'EPMSP'}</span>
                      {getStatutBadge(conv.statut)}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {conv.date_epreuve ? new Date(conv.date_epreuve).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '-'}
                      </span>
                      {conv.heure && <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{conv.heure}</span>}
                      {conv.lieu && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{conv.lieu}</span>}
                    </div>
                    {conv.notes && <p className="text-sm text-slate-500 italic">{conv.notes}</p>}
                  </div>

                  <div className="flex flex-col gap-2 shrink-0">
                    {/* Pending: Accept/Refuse buttons */}
                    {conv.statut === 'envoyee' && (
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleConvocationResponse(conv.convocation_id, 'acceptee')}
                          data-testid={`btn-accept-${conv.convocation_id}`}>
                          <CheckCircle className="h-4 w-4 mr-1" /> Accepter
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleConvocationResponse(conv.convocation_id, 'refusee')}
                          data-testid={`btn-refuse-${conv.convocation_id}`}>
                          <XCircle className="h-4 w-4 mr-1" /> Refuser
                        </Button>
                      </div>
                    )}

                    {/* Accepted: Add to calendar buttons */}
                    {conv.statut === 'acceptee' && (
                      <div className="flex flex-col gap-1.5">
                        <p className="text-xs font-medium text-green-700">Ajouter a votre agenda :</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="text-xs h-7 border-blue-200 text-blue-600 hover:bg-blue-50"
                            onClick={() => generateICalLink(conv)} data-testid={`btn-ical-${conv.convocation_id}`}>
                            <Download className="h-3 w-3 mr-1" /> iCal / Outlook
                          </Button>
                          <a href={generateGoogleCalLink(conv)} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="text-xs h-7 border-red-200 text-red-600 hover:bg-red-50"
                              data-testid={`btn-gcal-${conv.convocation_id}`}>
                              <ExternalLink className="h-3 w-3 mr-1" /> Google
                            </Button>
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardEvaluateur;
